import { CSSProperties, useEffect, useState } from 'react'
import { Container, Input, InputGroup, SelectPicker } from 'rsuite'
import { t } from 'i18next'
import SaveIcon from '@rsuite/icons/Check'
import CancelIcon from '@rsuite/icons/Close'
import Loading from '@/client/components/Loading'
import { PanelError } from '@client/components/ErrorProvider'
import { Button, IconButton, Table } from '@/client/components/Controls'
import useOutTemplateData from '../useOutTemplateData'
import { findTab } from '@client/stores/tabsStore'
import { syncTranslation } from '@/client/utils/translate'
import { Capabilities, hasPermission } from '@/shared'
import { TValueLabel } from '@/client/utils/types'
import { SearchIcon } from '@client/config'
import { useSyncedState } from '@client/utils/useSyncedState.ts'
import OutTableColumnModel from '@/client/models/outTableColumn'
import project from '@client/stores/projectStore'
import { serverLog } from '@client/utils/serverLog.ts'
import { user } from '@client/stores/authStore.ts'
import { useLayout } from '@client/components/Layout/LayoutManager.tsx'
import { getSocket } from '@client/socket/socket.ts'
import toastMessage from '@client/utils/toastMessage.tsx'
import { beginRowEdit, cancelRowEdit, endRowEdit } from '@client/utils/rowEdit'

/** What an edit of a template row can change, and so what cancel has to put back. */
const TEMPLATE_EDIT_FIELDS = ['type', 'format'] as const

const outTableColumnModel = new OutTableColumnModel()

type TFormat = {
    field?: string
    range?: any[]
    length?: number
    decimal?: number
    select?: any[]
    format?: string
    default?: string | number
    trueValue?: string
    falseValue?: string
}
type TFieldType = { value: string; label: string; format: TFormat }

type EditTamplateProps = { tabId: string }
type FormFieldTypeProps = { rowData: any; selectedType: string }
type EditableCellProps = { rowData?: any; dataKey?: any; onChange?: any; inTableData?: any; className?: string }
type ActionCellProps = { rowData?: any; dataKey?: any; onClick?: any; className?: string }
type FormatLabelProps = { rowData: any }

const UploadContentStyle = {
    minHeight: 36,
    display: 'flex',
    alignItems: 'center',
    padding: '0.75em 0.5em',
} as CSSProperties
const UploadButtonStyle = {
    textOverflow: 'ellipsis',
    overflow: 'hidden',
} as CSSProperties
const TabRowStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '1em',
} as CSSProperties
const TabRowLabelStyle = {
    minWidth: '11em',
    width: '15em',
    wordBreak: 'break-all',
    overflow: 'hidden',
    display: '-webkit-box',
    WebkitLineClamp: '1' /* number of lines to show */,
    lineClamp: '1',
    WebkitBoxOrient: 'vertical',
} as CSSProperties
const LabelStyle = {
    width: '3em',
} as CSSProperties
const FirstFieldLabelStyle = {
    marginLeft: 5,
    marginRight: 5,
    minWidth: '6em',
} as CSSProperties
const SecondFieldLabelStyle = {
    marginLeft: 15,
    marginRight: 5,
} as CSSProperties
const FieldTypeStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: 5,
    width: '100%',
    margin: '2px 0',
} as CSSProperties
const FieldTypeInputStyle = {
    background: 'var(--color-4)',
    color: 'var(--color-9)',
    border: 'none',
    flex: 1,
} as CSSProperties
const FieldTypeBooleanStyle = {
    background: 'var(--color-4)',
    color: 'var(--color-9)',
    border: 'none',
    flex: 1,
} as CSSProperties
const SelectPickerStyle = {
    borderRadius: 0,
    //minWidth: '8em',
    minWidth: '6em',
    flex: 1,
} as CSSProperties
const EditableFieldString = {
    display: 'flex',
    flexFlow: 'row nowrap',
    alignItems: 'center',
    gap: '0.5em',
    paddingLeft: '10px',
} as CSSProperties

// outside of components we have to sync the translations by our self
let typeLabels: TValueLabel[] = []
let fieldTypes: TFieldType[] = []
syncTranslation(() => {
    fieldTypes = [
        {
            value: 'string',
            label: t('Text'),
            format: {
                range: [1, 1024],
                default: 50,
            } as TFormat,
        },
        {
            value: 'number',
            label: t('Number'),
            format: {
                range: [0, 5],
                default: 2,
            },
        },
        {
            value: 'boolean',
            label: t('Boolean'),
            format: {
                range: [],
                default: '',
            },
        },
        {
            value: 'date',
            label: t('Date'),
            format: {
                range: [],
                default: 'dd.mm.yyyy',
            },
        },
        {
            value: 'select',
            label: t('Selection'),
            format: {
                range: [],
                default: '',
            },
        },
    ]

    typeLabels = [
        { value: 'format', label: t('Format') },
        { value: 'length', label: t('Length') },
        { value: 'decimal', label: t('Decimals') },
        { value: 'trueValue', label: t('True') },
        { value: 'falseValue', label: t('False') },
        { value: 'select', label: t('Selection') },
        { value: 'multiSelect', label: t('Multiselect') },
    ]
})

// const getFieldType = (type: string) => {
//     return fieldTypes.find((item) => {
//         return item.value == type
//     }) as TFieldType
// }

const getTypeLabel = (type: string) => {
    return typeLabels.find((item) => {
        return item.value == type
    })?.label
}

const EditableTypeCell = ({ rowData, dataKey, onChange, className = '', ...props }: EditableCellProps) => {
    const editing = false // rowData.status === 'EDIT'

    if (className) className += ' '
    className += editing ? 'table-content-editing' : ''

    const getLabel = (type: string) => {
        for (let i = 0; i < fieldTypes.length; i++) {
            if (fieldTypes[i].value == type) {
                return fieldTypes[i].label
            }
        }
        return fieldTypes[0].label
    }

    return (
        <Table.Cell {...props} className={className}>
            {editing ? (
                <SelectPicker
                    defaultValue={rowData[dataKey]}
                    data={fieldTypes}
                    cleanable={false}
                    searchable={false}
                    placement="auto"
                    placeholder={t('Select type ...')}
                    onSelect={(value) => {
                        onChange && onChange(rowData._id, dataKey, value)
                    }}
                />
            ) : (
                <span className="table-content-edit-span">{getLabel(rowData[dataKey])}</span>
            )}
        </Table.Cell>
    )
}

const FormatLabel = ({ rowData }: FormatLabelProps) => {
    const _select = (key: string, value: any) => {
        if (key == 'select') {
            const result: string[] = value
                .filter((item: any) => {
                    return !!item.label.trim()
                })
                .map((item: any) => {
                    return item.label
                })
            return result.join(', ')
        }
        if (key == 'multiSelect') {
            return Boolean(value) ? t('Yes') : t('No')
        }
        return value
    }
    const _s = (key: string, value: any) => {
        if (rowData.type == 'select') {
            return _select(key, value)
        }
        if (Array.isArray(value)) return _a(value)
        else if (typeof value == 'object') return _o(value)
        return value
    }
    const _a = (arr: any[]) => {
        const result: string[] = arr.map((value: any) => {
            return _s('', value)
        })
        return <span>{result.join(', ')}</span>
    }
    const _o = (obj: any) => {
        const result: any[] = Object.keys(obj).map((key: string, index: number) => {
            let value = obj[key]
            let label = getTypeLabel(key)
            value = _s(key, value)
            return (
                <tr key={index}>
                    <td className="label">{label}</td>
                    <td className="equal">=</td>
                    <td className="value">
                        <span>{value}</span>
                    </td>
                </tr>
            )
        })

        return result
    }

    return _o(rowData.format)
}

type KeyValue = { value: string; label: string }
type SelectProps = { data: KeyValue[] }
type SelectItemProps = { item: KeyValue }

const Select = ({ data }: SelectProps) => {
    return (
        <ul>
            {data
                ?.filter((item) => item.value != '0' && item.label != ' ')
                .map((item, index) => (
                    <SelectItem key={index} item={item} />
                ))}
        </ul>
    )
}

const SelectItem = ({ item }: SelectItemProps) => {
    return (
        <li key={item.value}>
            <Input name={item.value} value={item.label} />
        </li>
    )
}

const EditableFormatCell = ({ rowData, dataKey, onChange, className = '', ...props }: EditableCellProps) => {
    const editing = rowData.status === 'EDIT'
    const type = rowData.type || ''

    if (className) className += ' '
    className += editing ? 'table-content-editing' : ''

    function renderEditableField() {
        if (type == 'string')
            return (
                <Container style={EditableFieldString}>
                    <label>{t('Length')} =</label>
                    <Input
                        type="number"
                        className="source-input input-number"
                        defaultValue={rowData.format.length}
                        onChange={(value) => {
                            rowData.format.length = value
                        }}
                    />
                </Container>
            )
        if (type == 'number')
            return (
                <Container style={EditableFieldString}>
                    <label>{t('Decimals')} =</label>
                    <Input
                        type="number"
                        className="source-input input-number"
                        defaultValue={rowData.format.decimal}
                        onChange={(value) => {
                            rowData.format.decimal = value
                        }}
                    />
                </Container>
            )
        if (type == 'boolean')
            return (
                <Container style={EditableFieldString}>
                    <label>{t('True')} =</label>
                    <Input
                        type="text"
                        className="source-input input-number"
                        defaultValue={rowData.format.trueValue}
                        onChange={(value) => {
                            rowData.format.trueValue = value
                        }}
                    />
                    <label>{t('False')} =</label>
                    <Input
                        type="text"
                        className="source-input input-number"
                        defaultValue={rowData.format.falseValue}
                        onChange={(value) => {
                            rowData.format.falseValue = value
                        }}
                    />
                </Container>
            )
        if (type == 'select')
            return (
                <Container style={EditableFieldString} className="edit-out-table-select-list">
                    <Select data={rowData.format.select} />
                </Container>
            )
        return null
    }

    function renderStaticField() {
        return (
            <table className={`table-content-edit-span ${rowData.type}`}>
                <tbody>
                    <FormatLabel rowData={rowData} />
                </tbody>
            </table>
        )
    }

    return (
        <Table.Cell {...props} className={className}>
            {editing ? renderEditableField() : renderStaticField()}
        </Table.Cell>
    )
}
const ActionCell = ({ rowData, dataKey, onClick, className, ...props }: ActionCellProps) => {
    return (
        <Table.Cell {...props} className={className} align="center">
            {hasPermission(Capabilities.template_write) ? (
                rowData.status === 'EDIT' ? (
                    <>
                        <IconButton
                            className="save"
                            icon={<SaveIcon />}
                            title={t('Save')}
                            onClick={() => onClick(rowData._id, 'save')}
                        />
                        <IconButton
                            className="cancel"
                            icon={<CancelIcon />}
                            title={t('Cancel')}
                            onClick={() => onClick(rowData._id, 'cancel')}
                        />
                    </>
                ) : (
                    <Button style={{ width: '130px' }} appearance="link" onClick={() => onClick(rowData._id, 'edit')}>
                        {t('Edit')}
                    </Button>
                )
            ) : (
                <></>
            )}
        </Table.Cell>
    )
}

export default function EditTemplate({ tabId }: EditTamplateProps) {
    const tab = findTab(tabId)
    if (!tab) return null // or throw an error

    const { closeTab } = useLayout()
    const [rowData, setRowData] = useSyncedState(tab, 'rowData', [])
    const [refreshCounter, setRefreshCounter] = useSyncedState(tab, 'refreshCounter', 0)

    const [searchString, setSearchString] = useState<string>('')

    const { data, loading, error } = useOutTemplateData(tab)
    useEffect(() => {
        const id = String(tab?.store?.id || '')
        if (!id) return

        const socket = getSocket()

        const onResourceChanged = (payload: any) => {
            console.log(payload)
            // payload: { table: string, id?: string, action: 'updated' | 'deleted' }
            if (!payload) return
            if (payload.table !== 'outTemplate') return
            const ids: string[] = Array.isArray(payload.ids) ? payload.ids.map(String) : []
            if (ids.length > 0 && !ids.includes(id)) return
            // if ids is empty/undefined, treat it as “something changed”; sidebar can refetch, edit tab can ignore

            if (payload.action === 'deleted') {
                toastMessage(t('This item was deleted. Closing tab.'), 'warning')
                closeTab(tabId)
            }

            if (payload.action === 'updated') {
                setRefreshCounter(refreshCounter + 1)
            }
        }

        socket.on('resource:changed', onResourceChanged)
        return () => {
            socket.off('resource:changed', onResourceChanged)
        }
    }, [tab?.store?.id, tabId, closeTab, setRefreshCounter])

    useEffect(() => {
        if (data && rowData.length === 0) {
            // the template's columns in its order first, then the ones it no
            // longer has — the mapping list keeps those
            const columns: string[] = data.columns ?? []
            const ordered: any = [
                ...columns.map((key) => data.mapping.find((item: any) => item.column === key)).filter(Boolean),
                ...data.mapping.filter((item: any) => !columns.includes(item.column)),
            ]
            setRowData(ordered.map((item: any) => ({ ...item, status: null })))
        }
    }, [data])

    if (error) return <PanelError message={error.message} />
    if (loading) return <Loading />

    const onFormDataChanged = (data: any) => {
        //tab.store.formData = data
    }

    const filterTable = () => {
        return searchString
            ? // @ts-ignore
            rowData.filter((row) => row.field.toLowerCase().includes(searchString.toLowerCase()))
            : rowData
    }
    const handleSave = (row: any) => {
        console.log(row)
        serverLog.info(`User ${user.value.userName} edited template ${data._id}`)
        outTableColumnModel.updateMapping(project.currentProject.value?._id || '', data._id, row)
    }
    const handleChange = (id: string, key: string, value: any) => {
        const nextData: any = Object.assign([], rowData)
        nextData.find((item: any) => item._id === id)[key] = value
        setRowData(nextData)
    }
    const handleEditState = (id: string, mode: string) => {
        const nextData: any = Object.assign([], rowData)
        const activeItem: any = nextData.find((item: any) => item._id === id)
        if (activeItem) {
            if (mode == 'edit') {
                beginRowEdit(activeItem, TEMPLATE_EDIT_FIELDS)
                // fieldTypes.forEach(item => {
                //     //if (item.value) activeItem.edit[item.value] = activeItem.type == item.value ? activeItem.source : ''
                //     if (activeItem.type == item.value) {
                //         if (activeItem.type == 'string') {
                //             activeItem.edit[item.value] = activeItem.format.length
                //         } else {
                //             activeItem.edit[item.value] = ''
                //         }
                //     }
                // })
            } else if (mode == 'save') {
                activeItem.source = activeItem.edit[activeItem.type] || ''
                endRowEdit(activeItem)
                handleSave(activeItem)
            } /*if (mode == 'cancel')*/ else {
                const loadedItem: any = data.mapping.find((item: any) => item._id === id)
                cancelRowEdit(activeItem, loadedItem, TEMPLATE_EDIT_FIELDS)
            }
        }
        setRowData(nextData)
    }

    return (
        <Container className="mapping-list-wrapper">
            <Container className="search-container">
                <InputGroup className="search-bar">
                    <Input placeholder={t('Field')} onChange={setSearchString} />
                    <InputGroup.Addon>
                        <SearchIcon />
                    </InputGroup.Addon>
                </InputGroup>
            </Container>
            <div className="table-shell">
                <Table
                    data={filterTable()}
                    loading={loading}
                    rowKey="_id"
                    wordWrap="break-word"
                    showHeader
                    fillHeight
                    virtualized
                    rowClassName={(rowData) =>
                        (rowData?.status === 'EDIT' ? 'editing' : '') + ' editing-' + rowData?.type
                    }
                >
                    {/* field */}
                    <Table.Column align="left" flexGrow={1} resizable>
                        <Table.HeaderCell className="name">{t('Field')}</Table.HeaderCell>
                        <Table.Cell className="field ellipse" dataKey="field">
                            {(row: any) => row.field}
                        </Table.Cell>
                    </Table.Column>

                    {/* type */}
                    <Table.Column align="left" width={150} resizable>
                        <Table.HeaderCell className="type">{t('Type')}</Table.HeaderCell>
                        <EditableTypeCell className="type" dataKey="type" onChange={handleChange} />
                    </Table.Column>

                    {/* format */}
                    <Table.Column align="left" flexGrow={2} resizable>
                        <Table.HeaderCell className="format">{t('Format')}</Table.HeaderCell>
                        <EditableFormatCell className="format" dataKey="format" onChange={handleChange} />
                    </Table.Column>

                    {/* action button */}
                    <Table.Column align="center" width={130}>
                        <Table.HeaderCell>&nbsp;</Table.HeaderCell>
                        <ActionCell className="action" dataKey="_id" onClick={handleEditState} />
                    </Table.Column>
                </Table>
            </div>
        </Container>
    )
}
